// Camada de query dos relatórios da plataforma (`spec/tasks/WEB-28.md`,
// `spec/17-frontend.md` §8.2). Superfície admin: `/admin/v1/reports/*`, tipos de
// `types/admin.d.ts`.
//
// CONTRATO DE QUERY KEY (`app/live-events.ts`): domínio primeiro. As três keys daqui
// começam por `['reports','summary']` / `['reports','exports']` porque é por esse prefixo
// que o mapa de §4.5 as alcança, `report_export.ready`/`failed` invalidam a lista de
// exports e os eventos de transação invalidam o summary. O terceiro segmento (`platform`,
// `seller`, `admin`) separa do cache do seller (WEB-08), que vive no mesmo QueryClient.
//
// O que é regra de relatório e não de superfície (teto de 366 dias, rótulo de kind,
// `Retry-After` em minutos) é REUSADO de `queries/reports.ts` em vez de reescrito.
//
// Modo: o admin não tem toggle global (`spec/17` §4.4: "só no seller") e esta tela não
// manda `mode`; a API assume `live`, que é o que o backoffice olha. Ver "Questões
// abertas" da task.

import { queryOptions } from '@tanstack/react-query';
import { api } from '../client.ts';
import type { paths } from '../types/admin.d.ts';
import { EXPORTS_LIMIT, type IsoRange, isPeriodTooLong } from './reports.ts';

/** Corpo `200` de um `GET` do documento gerado. */
type Get200<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : never;

export type PlatformSummary = Get200<'/admin/v1/reports/platform'>;
export type AdminSellerSummary = Get200<'/admin/v1/reports/sellers/{id}'>;
export type AdminExportPage = Get200<'/admin/v1/reports/exports'>;
export type AdminExport = AdminExportPage['data'][number];
export type AdminExportKind = AdminExport['kind'];
export type TopSeller = PlatformSummary['top_sellers'][number];

export type CreateAdminExportBody =
  paths['/admin/v1/reports/exports']['post']['requestBody']['content']['application/json'];
export type AdminExportAccepted =
  paths['/admin/v1/reports/exports']['post']['responses'][202]['content']['application/json'];

export const PLATFORM_KEY = ['reports', 'summary', 'platform'] as const;
export const SELLER_SUMMARY_KEY = ['reports', 'summary', 'seller'] as const;
export const ADMIN_EXPORTS_KEY = ['reports', 'exports', 'admin'] as const;

/** Fallback obrigatório de §4.5 enquanto o SSE não está alcançável. */
const POLL_MS = 30_000;

/** Export `queued`/`running` não tem evento até terminar: polling curto. */
const PENDING_POLL_MS = 4_000;

export function isPendingAdminExport(item: AdminExport): boolean {
  return item.status === 'queued' || item.status === 'running';
}

export function adminPollIntervalFor(page: AdminExportPage | undefined): number {
  return page?.data.some(isPendingAdminExport) === true ? PENDING_POLL_MS : POLL_MS;
}

/**
 * Id público de seller (`zId('sel')`). Um `sel_` malformado viraria `400` com mensagem em
 * inglês: a tela nem chama e diz em pt-BR o que ela espera.
 */
const SELLER_ID = /^sel_[0-9A-Za-z]{6,}$/;

export function isSellerId(value: string): boolean {
  return SELLER_ID.test(value.trim());
}

/**
 * Todos os quatro kinds: o admin é justamente quem pode pedir `platform_summary_csv`
 * (`exports-routes.ts` devolve `403 insufficient_role` para o seller).
 */
export const ADMIN_EXPORT_KINDS = [
  'platform_summary_csv',
  'transactions_csv',
  'withdrawals_csv',
  'ledger_csv',
] as const satisfies readonly AdminExportKind[];

export function isAdminExportKind(value: string): value is AdminExportKind {
  return (ADMIN_EXPORT_KINDS as readonly string[]).includes(value);
}

/**
 * Sempre `from`/`to`, nunca `period`: mandar os dois é `400 validation_failed`, e o
 * `DateRangeFilter` já resolve todo preset em um par de datas.
 */
export function platformSummaryQueryOptions(range: IsoRange) {
  return queryOptions({
    queryKey: [...PLATFORM_KEY, range.from, range.to] as const,
    queryFn: () =>
      api<PlatformSummary>('/admin/v1/reports/platform', {
        query: { from: range.from, to: range.to },
      }),
    // Período acima do teto não vira request (o 400 seria certo, a mensagem é inglês).
    enabled: !isPeriodTooLong(range),
    refetchInterval: POLL_MS,
  });
}

/** `sellerId` vazio = nenhum seller escolhido: a query fica desligada, sem chamada. */
export function adminSellerSummaryQueryOptions(sellerId: string | undefined, range: IsoRange) {
  const id = sellerId ?? '';
  return queryOptions({
    queryKey: [...SELLER_SUMMARY_KEY, id, range.from, range.to] as const,
    queryFn: () =>
      api<AdminSellerSummary>(`/admin/v1/reports/sellers/${id}`, {
        query: { from: range.from, to: range.to },
      }),
    enabled: isSellerId(id) && !isPeriodTooLong(range),
    refetchInterval: POLL_MS,
  });
}

/**
 * A lista não é paginada na tela: `limit` 20 (o default da API) cobre o que um turno de
 * backoffice pede. Sem `useInfiniteQuery`, sem "carregar mais".
 */
export function adminExportsQueryOptions() {
  return queryOptions({
    queryKey: [...ADMIN_EXPORTS_KEY] as const,
    queryFn: () =>
      api<AdminExportPage>('/admin/v1/reports/exports', { query: { limit: EXPORTS_LIMIT } }),
    refetchInterval: ({ state }) => adminPollIntervalFor(state.data),
  });
}

/** `403 mfa_required` aqui abre o `StepUpDialog` e reexecuta, tudo em `api/client.ts`. */
export function requestAdminExport(body: CreateAdminExportBody): Promise<AdminExportAccepted> {
  return api<AdminExportAccepted>('/admin/v1/reports/exports', { method: 'POST', body });
}

/**
 * `download_url` só existe no GET do detalhe e só em `ready` dentro da validade
 * (`spec/13` §4.6): a listagem nunca traz a URL assinada, então o botão de download busca
 * o export na hora do clique.
 */
export function fetchAdminExport(id: string): Promise<AdminExport> {
  return api<AdminExport>(`/admin/v1/reports/exports/${id}`);
}
